import {arrayOfDatesToString, calculateCacheExpiration} from '@/shared/DateHelper'
/* import * as authRedirect from '@/auth/authRedirect' */
import axios from 'axios'
import {checkForSessionInvitation, prepareEventsList} from '@/shared/Utilities'
import dayjs from 'dayjs'
import {getSessionsWithRegistrations, getTimeslotsByEvent} from '@/shared/SessionHelper'
import {setNavigationVisibility, setRegistrationStatus} from '@/shared/StateHelper'
import {useApiStore} from '@/store/api'
import {useEventsStore} from '@/store/events'
import {useMsalStore} from '@/store/msal'
import * as authPopup from '@/auth/authPopup'  // ADD THIS
import authConfig from '@/auth/authConfig'

// Pinia stores are resolved lazily so this module is safe to static-import
// before `app.use(pinia)` runs (e.g. when a component pulled in by the
// initial bundle imports ApiHelper transitively). The Proxy preserves every
// existing `apiStore.x` / `msalStore.x` call site — including `this`-bound
// actions inside Pinia options-style stores, since `this` inside an action
// becomes the proxy and forwards property access/assignment back to the
// underlying store on first touch.
function lazyStore(useStoreFn) {
    let resolved
    return new Proxy({}, {
        get(_, key) {
            if (!resolved) resolved = useStoreFn()
            return resolved[key]
        },
        set(_, key, value) {
            if (!resolved) resolved = useStoreFn()
            resolved[key] = value
            return true
        },
    })
}
const apiStore = lazyStore(useApiStore)
const msalStore = lazyStore(useMsalStore)

let refreshPromise = null   // Deduplicates concurrent refresh attempts
let authFailed = false       // Circuit breaker: stops all API calls after auth failure

const addEmptySessions = async function(sessions) {
    if (0 < sessions.length) {
        const eventId = sessions[0].event.id
        let timeslots = await getTimeslotsByEvent(eventId)
        for (let i = 0; i < timeslots.length; ++i) {
            timeslots[i].hasSessions = false
            for (let j = 0; j < sessions.length; ++j) {
                if (sessions[j].timeslot.id == timeslots[i].id) {
                    timeslots[i].hasSessions = true
                    break
                }
            }
        }

        let newId = -1
        for (let i = 0; i < timeslots.length; ++i) {
            if (false == timeslots[i].hasSessions) {
                let session = {
                    id: --newId,
                    name: ' ',
                    code: null,
                    short_description: null,
                    description: null,
                    graphic: null,
                    event: {
                        id: eventId,
                        name: sessions[0].event.name,
                    },
                    timeslot: timeslots[i],
                    presenters: [],
                    facilitators: [],
                    tech_facilitators: [],
                    captioners: [],
                    translators: [],
                    room: null,
                    tracks: [],
                    teleconferencing_information: null,
                    stream_url: null,
                    recording_url: null,
                    conflict: false,
                    mandatory: false,
                    is_published: false,
                    admin_notes: '',
                    learning_objectives: [],
                    conflictingSessions: [],
                }
                sessions.push(session)
            }
        }
    }

    return sessions
}

// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
export const callApi = async function(endpoint, method = 'GET', data = {}, authOnly = false, csrfToken = null, ignoreNotFound = false, axiosConfig = {}) {
    if (authFailed && authOnly) {
        return null
    }
    if (!authFailed) {
        try {
          await refreshAccessToken()
        } catch (error) {
          console.error('Token refresh failed:', error)
          if (authOnly) {
              return null
          }
          // Session expired — redirect to sign-in instead of continuing with stale token
          if (window.location.pathname !== '/require-signin') {
              sessionStorage.setItem('signin_return_url', window.location.pathname + window.location.search)
          }
          window.location = '/require-signin?message=Your+session+has+expired.'
          return null
        }
    }
    let token = msalStore.accessToken
    if (authOnly && (null == token || '' == token)) {
        console.warn('Authentication required for this API call')
        return null
    }

    let headers = {}
    if (csrfToken) {
        headers = {'X-CSRFToken': csrfToken}
    }
    if (token) {
        headers.Authorization = `Bearer ${token}`
    }
    method = method.toUpperCase()
    let response = false

    const catchError = (error) => errorHandler(error, ignoreNotFound)
    try {
        switch (method) {
            case 'GET':
                response = await axios.get(endpoint, {headers: headers, ...axiosConfig}).catch(catchError)
                break
            case 'POST':
                response = await axios.post(endpoint, data, {headers: headers, ...axiosConfig}).catch(catchError)
                break
            case 'PUT':
                response = await axios.put(endpoint, data, {headers: headers, ...axiosConfig}).catch(catchError)
                break
            case 'PATCH':
                response = await axios.patch(endpoint, data, {headers: headers, ...axiosConfig}).catch(catchError)
                break
            case 'DELETE':
                response = await axios.delete(endpoint, {headers: headers, ...axiosConfig}).catch(catchError)
                break
            default:
                response = await axios.get(endpoint, {headers: headers, ...axiosConfig}).catch(catchError)
        }
        return response
    } catch (e) {
        return e.response
    }
}

async function refreshAccessToken() {
    if (authFailed) {
        return
    }
    if (refreshPromise) {
        return refreshPromise
    }

    const accounts = authPopup.msalAgent.getAllAccounts()
    if (accounts.length === 0) {
        return
    }
    const tokenRequest = {
        ...authConfig.tokenRequest,
        account: accounts[0],
    }

    refreshPromise = authPopup.msalAgent.acquireTokenSilent(tokenRequest)
        .then(response => {
            refreshPromise = null
            return response.accessToken
        })
        .catch(error => {
            refreshPromise = null
            authFailed = true
            msalStore.setAccessToken('')
            // Clear MSAL local cache so sign-in link works after redirect
            authPopup.msalAgent.clearCache()
            console.error('Silent token refresh failed:', error)
            throw error
        })

    return refreshPromise
}

const errorHandler = function(error, ignoreNotFound) {
    if (error.response) {
        if (404 == error.response.status && ignoreNotFound) {
            return error.response
        }

        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('error.response.status: ' + error.response.status)
        console.error('error.response.data: ' + error.response.data)

        // A lapsed session does not always answer 401. After a tab has sat idle for hours
        // the MSAL cache is empty and the silent refresh has failed, so the request goes
        // out with no credentials at all -- and the API answers 403, because no
        // authenticator recognises it and DRF raises NotAuthenticated. Treated as a plain
        // 4xx below, that dead-ends the person on /apierror when what they need is to sign
        // in again. Someone who IS signed in and gets a 403 is genuinely not permitted, and
        // still goes to /apierror (#995).
        const hasNoCredentials = authFailed || !msalStore.accessToken
        if (401 == error.response.status || (403 == error.response.status && hasNoCredentials)) {
            authFailed = true
            msalStore.setAccessToken('')
            authPopup.msalAgent.clearCache()
            if (window.location.pathname !== '/require-signin') {
                sessionStorage.setItem('signin_return_url', window.location.pathname + window.location.search)
            }
            window.location = '/require-signin?message=Your+session+has+expired.'
            return
        }
        if (404 == error.response.status) {
            window.location = '/notfound'
            return error.response
        }
        if (399 < error.response.status && 500 > error.response.status) {
            window.location = '/apierror'
            return
        }
    } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.error('error.request: ' + error.request)
    } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error', error.message)
    }
}

export const getAssetsByEventId = async function(eventId) {
    let assets = []
    const apiResponse = await callApi(apiStore.buildUrl('/events/assets/?event=' + eventId))
    if (apiResponse.data && Array.isArray(apiResponse.data)) {
        assets = apiResponse.data
    }

    return assets
}

export const getEventById = async function(eventId) {
    let currentEvent = apiStore.currentEvent
    if (null == currentEvent || eventId != currentEvent.event.id || dayjs() > currentEvent.expires) {
        apiStore.isVisibilitySet = false

        let idQueryString = ''
        if (null != apiStore.oid) {
            idQueryString = '?a=' + apiStore.oid
        }
        const eventIdWithOid = 0 < eventId ? eventId + '/' : ''

        const apiResponse = await callApi(apiStore.buildUrl('/events/events/' + eventIdWithOid + idQueryString))
        const event = apiResponse.data
        if (event.name) { // quick check that this probably is event data
            const expires = calculateCacheExpiration()

            apiStore.isAdmin = false
            if (null !== apiStore.currentAttendee) {
                for (let i = 0; i < event.admins.length; ++i) {
                    if (event.admins[i].id == apiStore.currentAttendee.id) {
                        console.log('Attendee ' + apiStore.currentAttendee.id + ' (' + apiStore.currentAttendee.display_name + ') is an admin for this event')
                        apiStore.isAdmin = true
                        break
                    }
                }
            }

            apiStore.currentEvent = {expires: expires, event: event}
            console.log('getEventById: Event ' + event.name + ' (' + eventId + ') found')
            storePersonalScheduleByEventId(eventId, true)
            if (apiStore.currentAttendee) {
                await storeRegistrationByEventIdAndAttendeeId(eventId, apiStore.currentAttendee.id)
            } else {
                apiStore.currentRegistration = null
            }
            if (apiStore.oid) {
                await setRegistrationStatus(apiStore)
            }
            await storeSessionsByEventId(eventId).then(() => {setNavigationVisibility(event, apiStore)})
            event.isCached = false
            event.dateString = arrayOfDatesToString(event.dates)
            apiStore.presenters = null

            // If the event is associated with more than one program partition, show learning objectives in schedules, not the Welcome
            const apiResponse = await callApi(apiStore.buildUrl('/programs/partitions/get_by_event/?event_id=' + event.id), 'GET', {}, false, null, true)
            if (apiResponse && apiResponse.data && apiResponse.data.partitions && 1 == apiResponse.data.partitions.length) {
                useEventsStore().isSingleProgramEvent = true
            }

            return event

        } else {
            console.warn('getEventById: No such event (id ' + eventId + ')')
            return null
        }
    } else {
        console.log('getEventById: Event ' + currentEvent.event.name + ' (' + eventId + ') retrieved from cache')
        currentEvent.event.isCached = true
        return currentEvent.event
    }
}

export const getEventsByDateRange = async function(startDate, endDate, filterByUser = false, sortDescending = false) {
    let queryString = 'startDate=' + startDate.format('YYYY-MM-DD') + '&endDate=' + endDate.format('YYYY-MM-DD')
    if (apiStore.isStaff) {
        queryString += '&staff=true'
    }
    if (filterByUser) {
        queryString += '&filter=true'
    }
    if (null !== apiStore.oid) {
        queryString += '&a=' + apiStore.oid
    }
    const apiResponse = await callApi(apiStore.buildUrl('/events/events/?' + queryString))
    const events = apiResponse.data

    if (Array.isArray(events)) { // quick check that this probably is events data
        return prepareEventsList(events, apiStore.isStaff, sortDescending)
    } else {
        return []
    }
}

export const getNonInstructionalSessionsByEventId = async function(eventId) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/sessions/?event=' + eventId + '&instructional=false'))
    return apiResponse.data
}

export const showFacilitatorGuide = function() {
    let isFacilitator = false

    if(!apiStore.currentEvent.event.has_facilitator_guide){
        return isFacilitator
    }

    if (apiStore.currentEvent.sessions && apiStore.currentAttendee) {
        outterloop: /* I can't belive JS has a goto all of this feels wrong */
        for (let session_idx  = 0; session_idx < apiStore.currentEvent.sessions.length; session_idx++) {
            for (let facilitator_idx  = 0; facilitator_idx< apiStore.currentEvent.sessions[session_idx].facilitators.length; facilitator_idx++) {
                if (apiStore.currentEvent.sessions[session_idx].facilitators[facilitator_idx].id == apiStore.currentAttendee.id) {
                        isFacilitator = true
                        break outterloop
                }
            }
        }
    }

    return isFacilitator
}

export const getFacilitatorGuide = async function(eventId) {
    if( apiStore.facilitatorGuide ){
        return apiStore.facilitatorGuide
    }
    const apiResponse = await callApi(apiStore.buildUrl('/events/facilitatorguide/?event=' + eventId))
    apiStore.facilitatorGuide = apiResponse.data[0]['facilitator_guide']
}

export const getPresentersByEventId = async function(eventId) {
    if (null == apiStore.presenters) {
        const apiResponse = await callApi(apiStore.buildUrl('/events/presenters/?event=' + eventId))
        const presenters = apiResponse.data
        apiStore.presenters = presenters
    }
    return apiStore.presenters
}

// Per-session presenter disclosures for the event, keyed by attendee id:
// { <attendeeId>: [{session_name, disclosures}, ...] }. Published presenters only.
export const getSessionDisclosuresByEventId = async function(eventId) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/session_disclosures/' + eventId))
    return apiResponse.data
}

export const storePersonalScheduleByEventId = async function(eventId, includeNonInstructional) {
    let sessions = await getSessionsWithRegistrations(eventId, true)
    if (false === Array.isArray(sessions)) { // quick check that this probably is events data
        throw new Error('Failed reading personal schedule sessions')
    }

    // My schedule shows only sessions this person is on. It used to append every
    // non-instructional session of the event here, whether or not they were on it, so
    // removing one brought it back on the next load as a ghost with an Add button --
    // the removal had worked, the schedule just re-added it to the display (#993).
    // Nothing attended is lost by dropping that: sessionsByAttendee already returns the
    // person's own non-instructional sessions, marked attending. Sessions somebody could
    // add belong in the full schedule, which is unchanged.
    if (false === includeNonInstructional) {
        // "Show only program sessions" now filters the person's own sessions, which is
        // the only thing left to filter.
        sessions = sessions.filter((session) => false === session.non_instructional)
    }

    sessions = sessions.filter((session) => null != session.timeslot) // remove sessions with no timeslot
    sessions = sessions.filter((session) => session.is_published) // remove unpublished sessions
    if (false == apiStore.isStaff && apiStore.currentAttendee) { // remove invitation-only sessions to which this attendee is not invited
        sessions = checkForSessionInvitation(sessions, apiStore.currentAttendee.id)
    }

    if (0 < sessions.length) {
        const today = dayjs()
        const visibleDate = sessions[0].event.visible_date ? dayjs(sessions[0].event.visible_date) : today
        const visibility = today >= visibleDate
        for(let i = 0; i < sessions.length; ++i){
            sessions[i]['showRoom'] = visibility
            sessions[i]['showMaterial'] = visibility
        }
    }

    const expires = calculateCacheExpiration()
    apiStore.mySchedule = {expires: expires, sessions: sessions}
}

export const storeRegistrationByEventIdAndAttendeeId = async function(eventId, attendeeId) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/eventattendees/getbyeventandattendee/?event_id=' +
                                                        eventId + '&attendee_id=' + attendeeId), 'GET', {}, false, null, true)
                                                        if (apiResponse && apiResponse.data && apiResponse.data.event_attendee) {
        apiStore.currentRegistration = apiResponse.data.event_attendee
    }
}

export const getCompletionStatus = async function(eventAttendeeId) {
    const apiResponse = await callApi(apiStore.buildUrl('/events/eventattendees/' + eventAttendeeId + '/completion_status/'),
                                      'GET', {}, false, null, true)
    if (apiResponse && apiResponse.data) {
        return apiResponse.data
    }
    return null
}

export const storeSessionsByEventId = async function(eventId) {
    if (apiStore.currentEvent && apiStore.currentEvent.event.id == eventId) {
        if (apiStore.currentEvent.sessions) {
            return
        }

        const startTime = Date.now()
        let sessions = await getSessionsWithRegistrations(eventId)
        console.log('Event sessions download time: ' + (Date.now() - startTime) + ' ms')

        sessions = sessions.filter((session) => null != session.timeslot)
        if (apiStore.isStaff) {
            sessions = await addEmptySessions(sessions)
        } else {
            sessions = sessions.filter((session) => session.is_published)
            sessions = checkForSessionInvitation(sessions, apiStore.oid)
        }

        apiStore.currentEvent.sessions = sessions
        apiStore.showFacilitatorGuideLink = showFacilitatorGuide()
    }
}

// Generous-but-finite upload timeout. axios's default of 0 = infinite is what
// produced the "I can't tell if it's working" UX when networks died silently.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

export const uploadFileToApi = async function(endpoint, file, onProgress = null) {
    const formData = new FormData()
    formData.append('name', file.name ? file.name : 'Temporary name')
    formData.append('description', file.description ? file.description : 'Temporary description')
    formData.append('short_description', file.description ? file.description : 'Temporary description')
    formData.append('uri', file.file)
    if(file.session){
       formData.append('session', file.session )
    }
    if(file.presenter){
       formData.append('presenter', file.presenter )
    }
    const axiosConfig = { timeout: UPLOAD_TIMEOUT_MS }
    if (onProgress) {
        axiosConfig.onUploadProgress = (e) => {
            const total = e.total || 0
            const percent = total ? Math.round((e.loaded / total) * 100) : 0
            onProgress({ loaded: e.loaded, total, percent })
        }
    }
    const response = await callApi(endpoint, 'POST', formData, false, null, false, axiosConfig)
    if (response && 200 <= response.status && 209 >= response.status){
        return response.data
    }
    return { error: (response && response.data) || (response && response.statusText) || 'upload failed' }
}

export const uploadFilesToApi = async function(endpoint, files) {
    const assets = []
    for (let i = 0; i < files.length; ++i) {
        const response = await uploadFileToApi(endpoint, files[i])
        assets.push(response)
        files[i].isUploaded = response.name && files[i].name == response.name
    }

    return assets
}
